import {
    Component, Mixins, Watch,
} from 'vue-property-decorator';
import HybridMixin from '@rogues/shared/mixins/hybrid';
import CoreMixin from '@nodes/mixins/Core';
import { isEqual } from 'lodash';
import {
    YInput, YCheckbox, YAccordionItem, YToggle, YChips, YIcon,
} from '@components/index';
import Props from './props';
    
@Component({
    components: {
        YInput,
        YCheckbox,
        YAccordionItem,
        YToggle,
        YChips,
        YIcon,
    },
})

/**
 * @name MarketFilter
 */
export default class MarketFilter extends Mixins(HybridMixin, Props, CoreMixin) {
    config: {[key: string]: any} = {}
    metadata: Array<object> = [];

    search = null
    available = this.$route.query.ware_is_available || 0
    hasDiscount = this.$route.query.has_discount || 0
    checkbox = {}
    categories = []
    showMobileFilter = false
    checkboxTimeout = null

    /**
     * Return categories list
     *
     * @returns {Array}
     */
    get categoriesList(): Array<any> {
        if (this.getValue('parentCategory')) {
            return this.categories.filter((item) => !item.parent_id);
        }
        return this.categories;
    }

    /**
     * Return applied filtersMixin
     *
     * @returns {Array}
     */
    get appliedFilters(): Array<string> {
        const query = { ...this.$route.query };
        let q : Array<string> = Object.keys(query)
            .filter((item) => !['page', 'sort', 'order'].includes(item))
            .filter((item) => query[item]);
        
        if (q.includes('tagging_group')) {
            q = q.filter((item) => item !== 'tagging_group');

            if (Array.isArray(query.tagging_group)) {
                query.tagging_group.forEach((element) => {
                    element.split(',').forEach((el) => {
                        q.push(`tagging_group:${el}`);
                    });
                });
            } else {
                query.tagging_group.split(',').forEach((el) => {
                    q.push(`tagging_group:${el}`);
                });
            }
        }

        if (q.includes('search_front')) {
            q = q.filter((item) => item !== 'search_front');
            q.push(`search_front:${query.search_front}`);
        }

        return q;
    }

    /**
     * Set value of query property in filter params
     */
    @Watch('$route.query', { immediate: true, deep: true })
    onRouteChange() {
        const query = { ...this.$route.query };
        if (query.tagging_group) {
            if (Array.isArray(query.tagging_group)) {
                query.tagging_group.forEach((element) => {
                    element.split(',').forEach((el) => {
                        this.checkbox[el] = 1;
                    });
                });
            } else {
                query.tagging_group.split(',').forEach((el) => {
                    this.checkbox[el] = 1;
                });
            }
        }
    }

    /**
     * Set new available status when value changes
     *
     * @param {*} val
     */
    @Watch('available')
    onAvailableChange(val: any) {
        if (val !== null) {
            this.$router.replace({
                query: {
                    ...this.$route.query,
                    page             : undefined,
                    ware_is_available: val === 1 ? val : undefined,
                },
            }).catch((err) => {
                this.handleError(err);
            });
        }
    }

    /**
     * Set new hasDiscount status when value changes
     *
     * @param {*} val
     */
    @Watch('hasDiscount')
    onHasDiscountChange(val: any) {
        if (val !== null) {
            this.$router.replace({
                query: {
                    ...this.$route.query,
                    page        : undefined,
                    has_discount: val === 1 ? val : undefined,
                },
            }).catch((err) => {
                this.handleError(err);
            });
        }
    }

    /**
     * Set new checkbox status when value changes
     *
     * @param {*} val
     */
    onCheckboxChange(val: any) {
        clearTimeout(this.checkboxTimeout);
        this.checkboxTimeout = setTimeout(() => {
            if (Object.keys(val).length) {
                const resetPage = !isEqual(Object.keys(val).filter((key) => (this.checkbox[key])), this.$route.query.tagging_group?.toString().split(','));

                // eslint-disable-next-line camelcase
                let tagging_group = [];
                // eslint-disable-next-line consistent-return
                Object.keys(val).forEach((key) => {
                    if (this.checkbox[key]) {
                        tagging_group.push(key);
                    } else if (this.checkbox[key] === 0) {
                        // eslint-disable-next-line camelcase
                        tagging_group = tagging_group.filter((item) => item !== key);
                        if (tagging_group.length) {
                            const query:any = {
                                ...this.$route.query,
                                tagging_group: tagging_group.join(','),
                            };
                            if (resetPage) {
                                delete query.page;
                            }
                            return this.$router.replace({ query }).catch((err) => {
                                this.handleError(err);
                            });
                        }

                        const query:any = {
                            ...this.$route.query,
                            tagging_group: tagging_group.join(','),
                        };
                        if (resetPage) {
                            delete query.page;
                        }
                        return this.$router.replace({ query }).catch((err) => {
                            this.handleError(err);
                        });
                    }
                });
                if (tagging_group.length) {
                    const query:any = {
                        ...this.$route.query,
                        tagging_group: tagging_group.join(','),
                    };

                    if (resetPage) {
                        delete query.page;
                    }
                    this.$router.replace({ query }).catch((err) => {
                        this.handleError(err);
                    });
                }
            }
        }, 1000);
    }
    
    /**
     * Show filter on mobile size
     */
    mobileFilterToggle() {
        this.showMobileFilter = !this.showMobileFilter;
    }

    /**
     * Do search
     */
    doSearch() {
        const query: any = {
            ...this.$route.query,
            search_front: this.search,
        };
        if (query.page) {
            delete query.page;
        }
        this.$router.replace({ query }).catch((err) => {
            this.handleError(err);
        });
    }
    
    /**
     * Return filter name
     * 
     * @param {*} value
     * @returns {string}
     */
    filterName(value: any): string {
        let labelTitle: string = '';

        if (value.startsWith('tagging_group')) {
            if (this.config?.labels) {
                Object.keys(this.config?.labels).forEach((k) => {
                    const findLabel = this.config?.labels[k].labels.find((c) => c.id === value.replace('tagging_group:', ''))?.title;
                    if (findLabel) {
                        labelTitle = findLabel;
                    }
                });
            }
            if (labelTitle) {
                return labelTitle;
            }

            const find = this.categories.find((c) => c.url_encode === value.replace('tagging_group:', ''))?.title;
            if (find) {
                return find;
            }
        }

        if (value.startsWith('search_front')) {
            return value.replace('search_front:', '');
        }

        return this.$tc(`market-filter.${value}`);
    }

    /**
     * reset All filters
     */
    resetFilters() {
        this.search = null;
        this.checkbox = {};
        this.$router.replace({
            query: {
                ...this.$route.query,
                tagging_group    : undefined,
                search_front     : undefined,
                ware_is_available: undefined,
                has_discount     : undefined,
            },
        });
    }

    /**
     * Remove a filter
     *
     * @param {string} slug
     */
    removeFilter(slug: string) {
        let s = slug;
        const query: any = {
            ...this.$route.query,
            page: undefined,
        };
        
        if (s.startsWith('tagging_group')) {
            s = s.replace('tagging_group:', '');
            if (Array.isArray(query.tagging_group)) {
                query.tagging_group = query.tagging_group.map((item) => item.split(',').filter((sp) => sp !== s).join(','));
            } else {
                query.tagging_group = query.tagging_group.split(',').filter((item) => item !== s).join(',');
            }
        } else if (s.startsWith('search_front')) {
            delete query.search_front;
        } else {
            delete query[s];
        }

        this.$router.replace({ query }).catch((err) => {
            this.handleError(err);
        });
    }

    /**
     * Fetch Config
     */
    fetchConfig() {
        this.$wait.start('loading-products-filters');

        const categories = this.$services.Tagging.labels({
            slug     : this.getValue('fetchParams.slug', 'products-category'),
            including: ['id', 'parent_id', 'title', 'url_encode', 'activeness'],
        }).catch((error: object) => {
            this.handleError(error);
        });

        const requests = [categories];

        if (this.getValue('fetchParams.tagging', this.$route.params.url_encode)) {
            const config = this.$services.Market.productsFilters({
                slug           : 'products',
                tagging        : this.$route.params.url_encode,
                'tagging:group': 'main',
                ...this.getValue('fetchParams'),
            }).catch((error: object) => {
                this.handleError(error);
            });
            requests.push(config);
        }

        Promise.all(requests).then((responses) => {
            if (this.handleReq(responses, 0)) {
                this.categories = responses[0].data.results;
            }

            if (this.handleReq(responses, 1)) {
                this.config = responses[1].data.results;
                this.metadata = responses[1].data.metadata;
            }
        }).catch((error: object) => {
            this.handleError(error);
        }).finally(() => {
            this.$wait.end('loading-products-filters');
        });
    }

    /**
     * Check the Promise.all Request Error
     * 
     * @param {Array} req - Responses array
     * @param {number} index - number of response
     * @returns {boolean}
     */
    handleReq(req: Array<any>, index: number): boolean {
        return req && req[index] && !req[index].isAxiosError;
    }

    /**
     * @inheritdoc
     */
    created() {
        this.fetchConfig();
    }
}
